import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

function getStoredToken() {
  return localStorage.getItem('admin_token');
}

function storeToken(token) {
  if (token) localStorage.setItem('admin_token', token);
}

function clearToken() {
  localStorage.removeItem('admin_token');
}

function authHeaders() {
  const token = getStoredToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_BACKEND_URL;

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) { setUser(false); setLoading(false); return; }
    try {
      const res = await fetch(`${API}/api/admin/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        setUser(await res.json());
      } else {
        clearToken();
        setUser(false);
      }
    } catch {
      setUser(false);
    }
    setLoading(false);
  }, [API]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password, totpCode) => {
    const body = { email, password };
    if (totpCode) body.totp_code = totpCode;
    const res = await fetch(`${API}/api/admin/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(formatError(data.detail));
    if (data.requires_2fa) return { requires_2fa: true };
    if (data.access_token) storeToken(data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    await fetch(`${API}/api/admin/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    clearToken();
    setUser(false);
  };

  const getAuthHeaders = () => authHeaders();

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, API, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAdminAuth() {
  return useContext(AuthContext);
}

function formatError(detail) {
  if (!detail) return 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}
